/**
 * One rep's forecast movement, decomposed (journey J4).
 *
 * ## Why this is not a red/green waterfall
 *
 * The repo's other bridge paints gains green and losses red, which is right for
 * a revenue bridge — there, down is bad. Here it is not. The entire argument of
 * B2 is that the honest number is frequently *lower*: a rep clearing quotes the
 * win curve wrote off months ago moves their month down, and that is a good
 * day's work. Colouring that red would assert the opposite of what the feature
 * is for, every time a rep did the right thing.
 *
 * So polarity is carried by the two things that cannot mislead — which side of
 * the running total the bar sits on, and a signed number written on it — and
 * colour is left to do one job: mark which bar has a person behind it. That is
 * the manager's actual question. It also sidesteps a real accessibility problem,
 * since a red/green pair at these steps fails CVD separation on a dark surface.
 *
 * Server composes, client renders: the endpoint returns the three causes
 * already summed and this component only projects them into geometry.
 */
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from "recharts";
import type { RepMovement } from "@shared-core/data/my-pipeline-types";
import {
  CAUSE_MEANING,
  toMovementSteps,
  type MovementStep,
} from "@shared-core/domain/forecast-movement";
import { formatCurrencyGbpCompact } from "@shared-core/domain/formatters";

// One hue for movement, neutral for the anchors. Identity comes from the axis
// label; polarity from position plus the signed label on each bar.
const ANCHOR_COLOR = "var(--color-muted-foreground)";
const MOVE_COLOR = "var(--color-primary)";

function colourFor(step: MovementStep): string {
  return step.kind === "anchor" ? ANCHOR_COLOR : MOVE_COLOR;
}

/**
 * Weight, not hue, marks the bar with a person behind it. The two causes
 * nobody chose recede; the human one holds full strength, because that is the
 * only bar a manager can do anything about.
 */
function opacityFor(step: MovementStep): number {
  if (step.kind === "anchor") return 0.35;
  return step.key === "intervention" ? 1 : 0.45;
}

function MovementTooltip(props: TooltipContentProps<number, string>) {
  if (!props.active || !props.payload?.length) return null;
  const step = props.payload[0]?.payload as MovementStep | undefined;
  if (!step) return null;
  return (
    <div className="max-w-xs rounded-md border border-border bg-card p-2.5 text-sm shadow-md">
      <p className="font-medium">{step.label}</p>
      <p className="tabular-nums">{step.display}</p>
      {step.kind === "delta" && step.key !== "opening" && (
        <p className="mt-1 text-xs text-muted-foreground">
          {CAUSE_MEANING[step.key as keyof typeof CAUSE_MEANING]}
        </p>
      )}
    </div>
  );
}

/**
 * The value written above each bar. The text arrives already formatted on the
 * datum (see `MovementStep.display`) so this only places it: polarity is
 * carried by this label rather than by colour, and it must not depend on the
 * bar's position in the series.
 */
function StepLabel(props: {
  x?: number;
  y?: number;
  width?: number;
  value?: string;
}) {
  const { x = 0, y = 0, width = 0, value } = props;
  if (!value) return null;
  return (
    <text
      x={x + width / 2}
      y={y - 6}
      textAnchor="middle"
      className="fill-foreground text-[10px] font-medium tabular-nums"
    >
      {value}
    </text>
  );
}

export function MovementBridgeChart({ rep }: { rep: RepMovement }) {
  const steps = toMovementSteps(rep);
  return (
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={steps}
          margin={{ top: 22, right: 8, bottom: 4, left: 8 }}
        >
          <CartesianGrid
            vertical={false}
            stroke="var(--color-border)"
            strokeDasharray="3 3"
          />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            interval={0}
            tick={{ fontSize: 11, fill: "var(--color-muted-foreground)" }}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            width={54}
            tick={{ fontSize: 11, fill: "var(--color-muted-foreground)" }}
            tickFormatter={(v: number) => formatCurrencyGbpCompact(v)}
          />
          <Tooltip
            cursor={{ fill: "var(--color-muted)", opacity: 0.3 }}
            content={MovementTooltip as never}
          />
          {/* Floating bars: an invisible base lifts each delta onto the running
              total, which is what makes a waterfall a waterfall. */}
          <Bar
            dataKey="base"
            stackId="a"
            fill="transparent"
            isAnimationActive={false}
          />
          <Bar
            dataKey={(d: MovementStep) => d.top - d.base}
            stackId="a"
            radius={[4, 4, 0, 0]}
            isAnimationActive={false}
          >
            {steps.map((step) => (
              <Cell
                key={step.key}
                fill={colourFor(step)}
                fillOpacity={opacityFor(step)}
              />
            ))}
            <LabelList dataKey="display" content={StepLabel as never} />
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
